import React from 'react';
import { Box, Chip, Typography, Skeleton, useTheme } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { RegimeIndex, RegimePoint } from '../../hooks/useStatisticsPanels';
import { pnlColors } from '../../design/theme';
import { formatCurrency, formatShortDate, formatCompactCurrency } from '../../design/format';

const CHART_HEIGHT = 200;

// Subtle status-chip tints derived from pnlColors (matches the Sell-chip
// precedent in CandleChartModal, where rgba tints of the P&L colors are used).
const GAIN_TINT = 'rgba(46, 125, 50, 0.12)'; // pnlColors.gain at 12%
const LOSS_TINT = 'rgba(198, 40, 40, 0.12)'; // pnlColors.loss at 12%

interface RegimeTooltipProps extends TooltipProps<number, string> {
  symbol: string;
}

const RegimeTooltip: React.FC<RegimeTooltipProps> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload as RegimePoint;
  return (
    <Box sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
        {new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {`Close ${formatCurrency(row.close)}`}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {`200d MA ${row.ma !== null ? formatCurrency(row.ma) : '—'}`}
      </Typography>
    </Box>
  );
};

interface RegimeCardProps {
  index: RegimeIndex | undefined;
  filterEnabled: boolean | undefined;
  loading: boolean;
}

export const RegimeCard: React.FC<RegimeCardProps> = ({ index, filterEnabled, loading }) => {
  const theme = useTheme();

  if (loading) {
    return <Skeleton variant="rounded" height={240} />;
  }

  if (!index || index.data.length === 0) {
    return (
      <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data — benchmark fetch failed
        </Typography>
      </Box>
    );
  }

  const symbol = index.symbol;
  const current = index.current;

  const statusChip = () => {
    if (!current || current.above === null) {
      return <Chip size="small" label="Insufficient data" sx={{ fontWeight: 700 }} />;
    }
    if (current.above) {
      return (
        <Chip size="small" label="Above 200-day MA" sx={{ fontWeight: 700, bgcolor: GAIN_TINT, color: pnlColors.gain }} />
      );
    }
    return (
      <Chip size="small" label="Below 200-day MA" sx={{ fontWeight: 700, bgcolor: LOSS_TINT, color: pnlColors.loss }} />
    );
  };

  // Neutral MA line so it reads as a reference, not a second series: theme
  // secondary text color, which stays legible against the primary close line.
  const maStroke = theme.palette.text.secondary;

  const description = `${symbol} close versus its 200-day moving average over the last six months, ${
    current && current.above !== null
      ? `currently ${current.above ? 'above' : 'below'} the moving average`
      : 'with insufficient history to compute the moving average'
  }.`;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1 }}>
        {statusChip()}
        {current && (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {`Close ${formatCurrency(current.close)} · MA ${current.ma !== null ? formatCurrency(current.ma) : '—'}`}
          </Typography>
        )}
        {filterEnabled !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ marginLeft: 'auto' }}>
            {`Filter ${filterEnabled ? 'on' : 'off'} for this trader`}
          </Typography>
        )}
      </Box>

      <Box sx={{ position: 'relative', width: '100%', height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={index.data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatShortDate}
              tickLine={false}
              axisLine={false}
              fontSize={11}
              interval="preserveStartEnd"
              minTickGap={40}
              stroke={theme.palette.text.secondary}
            />
            <YAxis
              tickFormatter={formatCompactCurrency}
              domain={['auto', 'auto']}
              tickLine={false}
              axisLine={false}
              fontSize={11}
              width={48}
              stroke={theme.palette.text.secondary}
            />
            <Tooltip content={<RegimeTooltip symbol={symbol} />} cursor={{ stroke: theme.palette.divider }} />
            <Legend />
            <Line
              type="monotone"
              dataKey="close"
              name="Close"
              stroke={theme.palette.primary.main}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="ma"
              name="200d MA"
              stroke={maStroke}
              strokeDasharray="6 3"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
        <Box
          role="img"
          aria-label={description}
          sx={{ position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
        >
          {description}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Last ~6 months · same 200-day MA the buy gate checks
      </Typography>
    </Box>
  );
};
